import { useEffect, useLayoutEffect, useState } from 'react';
import { getApiUrl } from '@/lib/apiUrl';

// useShowPricing — may the landing page render its pricing UI (the teaser
// section and the "Pricing" links in the navbar and footer)?
//
// The answer is an admin switch served by GET /api/site-settings, so it is
// not known when the page first renders. The page used to assume "yes" and
// take the pricing UI back out when the answer was "no": hidden pricing
// flashed on every mount, the page jumped by the height of the section, and
// Back landed that far from where the visitor had been.
//
// Rules:
//   - Not known yet -> no pricing UI. "Not known" and "hidden" draw the same
//     page, so a late answer can only ever add pricing, never flash it.
//   - The last answer is kept for the tab's session (in memory, then
//     sessionStorage) and seeds the first render of every later mount —
//     reload, Back, a return from another page — so the page does not change
//     size after it mounts, whichever way the switch is set. Session-scoped
//     on purpose: a remembered "on" must not outlive the visit it was true in.
//   - index.js asks as the app boots when the first page is the logged-out
//     homepage, so the request is already on its way when the page mounts.
//     The page itself waits for nothing (it is in the app's first frame), so
//     on the first view in a tab the answer normally arrives after it: with
//     pricing on, the teaser and the two links are added at that moment — a
//     late add, never a flash. Every later mount starts from the remembered
//     answer and has them in its first frame.
//   - A mount asks again unless the answer is under FRESH_MS old, so a change
//     made in the admin panel shows up on the next mount.
//   - Request failed -> keep the last answer if there is one (a failed
//     request says nothing new); with no answer at all, pricing stays hidden.
const KEY = 'nsofar.showPricing';
const FRESH_MS = 30 * 1000;

let known; // undefined until there is an answer, then true | false
let answeredAt = 0; // when `known` was last confirmed during this page load
let inFlight = null;

function lastKnown() {
  if (known === undefined) {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (raw === 'true') known = true;
      else if (raw === 'false') known = false;
    } catch {
      // Storage blocked — the in-memory value still covers this page load.
    }
  }
  return known;
}

// Also called by the admin switch right after it saves, so the admin's own
// next look at the landing page starts from the value they just set.
export function rememberShowPricing(value) {
  if (typeof value !== 'boolean') return;
  known = value;
  answeredAt = Date.now();
  try {
    sessionStorage.setItem(KEY, String(value));
  } catch {
    // Storage blocked or full — the in-memory value is enough.
  }
}

// One request at a time. Resolves to the best answer available afterwards:
// the server's when the request worked, otherwise whatever was known before.
function ask() {
  if (known !== undefined && Date.now() - answeredAt < FRESH_MS) return Promise.resolve(known);
  if (!inFlight) {
    inFlight = fetch(`${getApiUrl()}/api/site-settings`)
      .then(r => {
        if (!r.ok) throw new Error(`site-settings ${r.status}`);
        return r.json();
      })
      .then(data => rememberShowPricing(data?.show_pricing))
      .catch(() => {
        // Deliberate: carry on with whatever was known before.
      })
      .then(() => {
        inFlight = null;
        return lastKnown();
      });
  }
  return inFlight;
}

export function prefetchShowPricing() {
  ask();
}

export function useShowPricing() {
  const [show, setShow] = useState(() => lastKnown() === true);

  // Rendering and showing are not the same moment: a navigation renders in a
  // transition, which React can pause before it commits. Read again at
  // commit, before the browser paints, so an answer that arrived in that gap
  // is in the first frame instead of one frame late.
  useLayoutEffect(() => {
    setShow(lastKnown() === true);
  }, []);

  useEffect(() => {
    let mounted = true;
    ask().then(latest => {
      if (mounted && latest !== undefined) setShow(latest);
    });
    return () => { mounted = false; };
  }, []);

  return show;
}
